import type { JSX } from 'react'
import { Pencil, Send, Trash2 } from 'lucide-react'
import type { AlertRuleDto, AlertTestResultDto } from '@/lib/dto'
import { dateTimeOf } from '@/ui/format'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { OnTop, Surface } from '@/ui/primitives/Surface'
import { ALERT_CHANNEL_META, ALERT_KIND_META } from './alert-model'
import { AlertTestResult } from './AlertTestResult'

/**
 * Карточка правила.
 *
 * Порядок сведений — по частоте вопросов: «о чём это правило», «работает ли
 * оно сейчас», «куда придёт сообщение», «когда стреляло в последний раз».
 * Последнее срабатывание важнее даты создания: правило, не сработавшее ни разу,
 * — это либо «всё хорошо», либо «оно сломано», и отличить одно от другого
 * помогает кнопка проверки доставки рядом.
 *
 * Клик по всей карточке открывает правку: отдельной страницы у правила нет.
 */

export interface AlertRuleTileProps {
  rule: AlertRuleDto
  testing?: boolean
  /** Итог последней проверки доставки этого правила в текущей сессии. */
  testResult?: AlertTestResultDto | null
  onTest: () => void
  onEdit: () => void
  onDelete: () => void
}

export function AlertRuleTile({
  rule,
  testing = false,
  testResult = null,
  onTest,
  onEdit,
  onDelete
}: AlertRuleTileProps): JSX.Element {
  const kind = ALERT_KIND_META[rule.kind]
  const lastFired = dateTimeOf(rule.lastFiredAt)

  return (
    <Surface
      variant="raised"
      onActivate={onEdit}
      activateLabel={`Открыть правило ${rule.name}`}
      className="flex h-full flex-col"
      data-alert-id={rule.id}
    >
      <div className="flex min-w-0 flex-col gap-1 px-3.5 pt-3">
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <p className="truncate font-display text-md font-semibold text-ink">{rule.name}</p>
          {/* Точка + текст: состояние не должно передаваться одним цветом. */}
          <Chip tone={rule.enabled ? 'live' : 'idle'} dot>
            {rule.enabled ? 'Включено' : 'Выключено'}
          </Chip>
        </div>
        <p className="truncate text-xs text-ink-muted">{kind.label}</p>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-1 px-3.5 text-2xs">
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <dt className="shrink-0 text-ink-muted">Канал</dt>
          <dd className="truncate text-ink">{ALERT_CHANNEL_META[rule.channel].label}</dd>
        </div>
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <dt className="shrink-0 text-ink-muted">Адрес</dt>
          {/* Только маска: сырого адреса у панели нет и быть не может. */}
          <dd className="truncate font-mono text-ink" title={rule.targetMasked}>
            {rule.targetMasked}
          </dd>
        </div>
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <dt className="shrink-0 text-ink-muted">Последнее срабатывание</dt>
          <dd className="truncate font-mono text-ink tabular-nums">{lastFired ?? 'ещё ни разу'}</dd>
        </div>
      </dl>

      <p className="mt-2.5 px-3.5 pb-1 text-2xs text-ink-faint">{kind.when}</p>

      <OnTop className="mt-auto border-t border-line px-2 py-1.5">
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="secondary"
            loading={testing}
            title="Отправить пробное сообщение по этому адресу"
            onClick={onTest}
          >
            <Send aria-hidden className="size-3.5" />
            Проверить доставку
          </Button>

          <IconButton
            label={`Изменить правило ${rule.name}`}
            size="sm"
            className="ml-auto"
            onClick={onEdit}
          >
            <Pencil aria-hidden className="size-4" />
          </IconButton>
          <IconButton
            label={`Удалить правило ${rule.name}`}
            size="sm"
            tone="danger"
            onClick={onDelete}
          >
            <Trash2 aria-hidden className="size-4" />
          </IconButton>
        </div>

        {/* Итог проверки — здесь же, у кнопки, которую нажали. */}
        {testResult === null ? null : (
          <div className="mt-1.5">
            <AlertTestResult result={testResult} />
          </div>
        )}
      </OnTop>
    </Surface>
  )
}
